import {
  faArrowAltCircleLeft,
  faEye,
  faSquare,
  faTimesCircle,
  faQuestionCircle,
  faCheckCircle,
} from "@fortawesome/free-regular-svg-icons";
import {
  faStream as fasStream,
  faThumbsUp as fasThumbsUp,
  faCheckCircle as fasCheckCircle,
  faDotCircle as fasDotCircle,
  faBan,
  faPlayCircle,
  faHandshake,
  faPauseCircle,
  faArrowsAlt,
  faMoneyBillAlt,
  faUserNurse,
  faCalendarCheck,
  faLaptopHouse,
  faUserCheck,
  faArrowAltCircleUp,
  faFilePdf,
  faFileImage,
  faFile,
  faFileVideo,
  faFileExcel,
  faFileWord,
  faTrash,
} from "@fortawesome/free-solid-svg-icons";
import { toast } from "react-toastify";
import i18n from "i18next";
import moment from "moment";
import lodash from "lodash";
import { matchPath } from "react-router";
import config from "config";
import { v1 } from "uuid";
import { asynchronousServiceCall } from "@odm/services/asynchronousServiceCall";
import DOMPurify from "dompurify";

export const isValidUrl = (url) => {
  try {
    new URL(url);
    return true;
  } catch (error) {
    return false;
  }
};

export const entityPermissionAllowed = (data) => {
  //console.log("===> Checking entity permission", data);

  if (data?.entityPermissionsError) {
    //console.log("===> Denied due to permission error");
    return false;
  }

  if (!data?.entityPermissionsLoaded) {
    //console.log("===> Denied due to permission not loaded");
    return false;
  }

  if (!data?.permissionsRequired) {
    //console.log("===> Denied due to no permission provided");
    return false;
  }

  // Check if any required permission exists in entityPermissions
  const hasPermission = data?.permissionsRequired?.some((p) =>
    data?.entityPermissions?.includes(p)
  );

  if (hasPermission) {
    //console.log("===> Allowed. Permission found");
    return true;
  }

  //console.log("===> Denied. Permission not found");
  return false;
};

export const validateIban = (iban, t) => {
  //console.log("validateIban---", { iban: iban, t: t });
  // Remove spaces and convert to uppercase
  const formattedIban = iban?.replace(/\s/g, "")?.toUpperCase();

  if (!formattedIban) {
    return {
      isValid: false,
      error: t ? t("common:IBANisRequired") : "IBAN is required.",
    };
  }

  // IBAN must be at least 15 chars and no more than 34 chars
  if (formattedIban.length < 15 || formattedIban.length > 34) {
    return {
      isValid: false,
      error: t
        ? t("common:IBANlengthMustBeBetween15And34Characters")
        : "IBAN length must be between 15 and 34 characters.",
    };
  }

  // First 2 chars must be letters (country code)
  const countryCode = formattedIban.substring(0, 2);
  if (!/^[A-Z]{2}$/.test(countryCode)) {
    return {
      isValid: false,
      error: t
        ? t("common:IBANmustStartWithAValid2LetterCountryCode")
        : "IBAN must start with a valid 2-letter country code.",
    };
  }

  // Next 2 chars must be numbers (check digits)
  const checkDigits = formattedIban.substring(2, 4);
  if (!/^\d{2}$/.test(checkDigits)) {
    return {
      isValid: false,
      error: t
        ? t("common:IBANcheckDigitsMustBeNumbers")
        : "IBAN check digits must be numbers.",
    };
  }

  // Rest of IBAN must be alphanumeric
  const bban = formattedIban.substring(4);
  if (!/^[A-Z0-9]+$/.test(bban)) {
    return {
      isValid: false,
      error: t
        ? t("common:IBANcanOnlyContainLettersAndNumbers")
        : "IBAN can only contain letters and numbers.",
    };
  }

  // Validate check digits by moving first 4 chars to end and converting letters to numbers
  const rearrangedIban = bban + countryCode + checkDigits;
  const convertedIban = rearrangedIban
    .split("")
    .map((char) => {
      if (/[0-9]/.test(char)) {
        return char;
      }
      // Convert A=10, B=11, etc
      return (char.charCodeAt(0) - 55).toString();
    })
    .join("");

  // Check if mod 97 equals 1
  let remainder = 0;
  for (let i = 0; i < convertedIban.length; i++) {
    remainder = (remainder * 10 + parseInt(convertedIban[i])) % 97;
  }

  if (remainder !== 1) {
    return {
      isValid: false,
      error: t
        ? t("common:InvalidIBANcheckDigits")
        : "Invalid IBAN (check digits failed).",
    };
  }

  return {
    isValid: true,
    error: null,
    countryCode: countryCode,
  };
};

export const yes = () => {
  return true;
};

export const no = () => {
  return false;
};

export const hasAnyOfTheRoles = (userRoles, rolesList) => {
  if (!userRoles || !rolesList) return false;

  // Convert to arrays if they're not already
  const userRolesArray = Array.isArray(userRoles) ? userRoles : [userRoles];
  const rolesListArray = Array.isArray(rolesList) ? rolesList : [rolesList];

  // Check if any of the user's roles are in the allowed roles array
  return userRolesArray.some((role) => rolesListArray.includes(role));
};

export const hasAnyOfThePermissions = (userPermissions, permissionsList) => {
  if (!userPermissions || !permissionsList) return false;

  const userPermissionsArray = Array.isArray(userPermissions)
    ? userPermissions
    : [userPermissions];
  const permissionsListArray = Array.isArray(permissionsList)
    ? permissionsList
    : [permissionsList];

  // Check if any of the user's permissions are in the allowed permissions array
  return userPermissionsArray.some((permission) =>
    permissionsListArray.includes(permission)
  );
};

export const getEnvironment = async () => {
  const response = await fetch(process.env.PUBLIC_URL + "/environment.json", {
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
    },
  });
  return await response.json();
};

export const userHasOnlyFARRoles = (roles) => {
  if (
    Array.isArray(roles) &&
    roles.length > 0 &&
    roles.every((role) => role === "PFMSpecialist" || role === "FARSubmitter")
  ) {
    return true;
  }
  return false;
};

export const isDev = (env, method = false) => {
  //Detection by url
  if (method === "location") {
    let allowed = ["localhost", "clientgateway-dev.isdb.org"];
    if (allowed.includes(window.location.hostname)) {
      //console.log("--- Is DEV");
      return true;
    }
  }

  //Fallback to env detection
  return env?.name === "dev" || env?.name === "development";
};

export const isTest = (env, method = false) => {
  //Detection by url
  if (method === "location") {
    if (window.location.hostname === "clientgateway-qa.isdb.org") {
      //console.log("--- Is QA");
      return true;
    }
  }

  //Fallback to env detection
  return env?.name === "qa" || env?.name === "test";
};

export const isProd = (env, method = false) => {
  //Detection by url
  if (method === "location") {
    if (window.location.hostname === "clientgateway.isdb.org") {
      //console.log("--- Is PRD");
      return true;
    }
  }

  //Fallback to env detection
  return (
    env?.name === "prd" || env?.name === "prod" || env?.name === "production"
  );
};

export const generateRequestId = function () {
  // Math.random should be unique because of its seeding algorithm.
  // Convert it to base 36 (numbers + letters), and grab the characters
  // after the decimal.
  // return Math.random().toString(36).substr(2);
  //

  // or just return a uuid
  return randomTraceId(); //uuid();
};

// === Generate a random 64-bit number in fixed-length hex format
export const randomTraceId = function () {
  const digits = "0123456789abcdef";
  let n = "";
  for (let i = 0; i < 16; i += 1) {
    const rand = Math.floor(Math.random() * 16);
    n += digits[rand];
  }
  return n;
};

export const uuid = function () {
  return v1();
};

export const isIterableArray = (array) =>
  Array.isArray(array) && !!array.length;

//===============================
// Breakpoints
//===============================
export const breakpoints = {
  xs: 0,
  sm: 576,
  md: 768,
  lg: 992,
  xl: 1200,
  xxl: 1540,
};

//===============================
// Store
//===============================
export const getItemFromStore = (key, defaultValue, store = localStorage) => {
  try {
    return JSON.parse(store.getItem(key)) || defaultValue;
  } catch (error) {
    console.log(error);
    return null;
  }
};
export const setItemToStore = (key, payload, store = localStorage) =>
  store.setItem(key, JSON.stringify(payload));
export const removeItemFromStore = (key, store = localStorage) =>
  store.removeItem(key);
export const getStoreSpace = (store = localStorage) =>
  parseFloat(
    (
      escape(encodeURIComponent(JSON.stringify(store))).length /
      (1024 * 1024)
    ).toFixed(2)
  );

//===============================
// Cookie
//===============================
export const getCookieValue = (name) => {
  const value = document.cookie.match(
    "(^|[^;]+)\\s*" + name + "\\s*=\\s*([^;]+)"
  );
  return value ? value.pop() : null;
};

export const createCookie = (name, value, cookieExpireTime) => {
  const date = new Date();
  date.setTime(date.getTime() + cookieExpireTime);
  const expires = "; expires=" + date.toUTCString();
  document.cookie = name + "=" + value + expires + "; path=/";
};

//===============================
// Colors
//===============================
export const hexToRgb = (hexValue) => {
  let hex;
  hexValue.indexOf("#") === 0
    ? (hex = hexValue.substring(1))
    : (hex = hexValue);
  // Expand shorthand form (e.g. "03F") to full form (e.g. "0033FF")
  const shorthandRegex = /^#?([a-f\d])([a-f\d])([a-f\d])$/i;
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(
    hex.replace(shorthandRegex, (m, r, g, b) => r + r + g + g + b + b)
  );
  return result
    ? [
      parseInt(result[1], 16),
      parseInt(result[2], 16),
      parseInt(result[3], 16),
    ]
    : null;
};

export const rgbColor = (color) => `rgb(${hexToRgb(color)})`;
export const rgbaColor = (color, alpha = 0.5) =>
  `rgba(${hexToRgb(color)},${alpha})`;

//===============================
// Echarts
//===============================
export const getPosition = (pos, params, dom, rect, size) => ({
  top: pos[1] - size.contentSize[1] - 10,
  left: pos[0] - size.contentSize[0] / 2,
});

//===============================
// E-Commerce
//===============================
export const calculateSale = (base, less = 0, fix = 2) =>
  (base - base * (less / 100)).toFixed(fix);
export const getTotalPrice = (cart, baseItems) =>
  cart.reduce((accumulator, currentValue) => {
    const { id, quantity } = currentValue;
    const { price, sale } = baseItems.find((item) => item.id === id);
    return accumulator + calculateSale(price, sale) * quantity;
  }, 0);

//===============================
// Helpers
//===============================

export const getPaginationArray = (totalSize, sizePerPage) => {
  const noOfPages = Math.ceil(totalSize / sizePerPage);
  const array = [];
  let pageNo = 1;
  while (pageNo <= noOfPages) {
    array.push(pageNo);
    pageNo = pageNo + 1;
  }
  return array;
};

export const isPagingButtonEnabled = (pageNo, paging) => {
  // console.log("PageNo:", pageNo);
  // console.log("PagingData:", paging);

  if (paging?.currentPage + 1 === pageNo) {
    return true;
  }

  return false;
};

export const isPageFillers = (pageNo) => {
  if (pageNo === "...") {
    return true;
  }

  return false;
};

export const getPaginationArrayWithPageScrollArray = (
  noOfPages,
  paginationSize,
  currPage,
  lastPageText,
  firstPageText,
  pageStartIndex
) => {
  var lastPage = 0;

  if (currPage + paginationSize < noOfPages) {
    lastPage = currPage + paginationSize;
  } else {
    lastPage = noOfPages;
  }

  let pages = [];
  let endPage = noOfPages;
  if (endPage <= 0) return [];

  let startPage = Math.max(
    currPage - Math.floor(paginationSize / 2),
    pageStartIndex
  );
  endPage = startPage + paginationSize - 1;

  if (endPage > lastPage) {
    endPage = lastPage;
    startPage = endPage - paginationSize + 1;
  }

  if (
    startPage !== pageStartIndex &&
    noOfPages > paginationSize &&
    pages.length === 0
  ) {
    pages = [firstPageText];
  }

  for (let i = startPage; i <= endPage; i += 1) {
    if (i >= pageStartIndex) pages.push(i);
  }

  // if ((endPage <= lastPage && pages.length > 1)) {
  //   pages.push(nextPageText);
  // }

  if (endPage !== lastPage) {
    pages.push("...");
    pages.push(lastPageText);
  }

  //console.log("pages data", pages);

  return pages;
};

export const getPaginationArrayWithPageScroll = (
  totalSize,
  sizePerPage,
  paginationSize,
  currPage,
  pageStartIndex,
  lastPageText,
  firstPageText
) => {
  const noOfPages = Math.ceil(totalSize / sizePerPage);
  const array = [];
  // let count = 0;
  // let pageNo = currPage;
  // while (pageNo <= noOfPages && count !== paginationSize) {
  //   array.push(pageNo);
  //   pageNo = pageNo + 1;
  //   count = count + 1;
  // }

  var lastPage = 0;

  if (currPage + paginationSize < noOfPages) {
    lastPage = currPage + paginationSize;
  } else {
    lastPage = noOfPages;
  }

  let pages = [];
  let endPage = noOfPages;
  if (endPage <= 0) return [];

  let startPage = Math.max(
    currPage - Math.floor(paginationSize / 2),
    pageStartIndex
  );
  endPage = startPage + paginationSize - 1;

  if (endPage > lastPage) {
    endPage = lastPage;
    startPage = endPage - paginationSize + 1;
  }

  if (
    startPage !== pageStartIndex &&
    noOfPages > paginationSize &&
    pages.length === 0
  ) {
    pages = [firstPageText];
  }

  for (let i = startPage; i <= endPage; i += 1) {
    if (i >= pageStartIndex) pages.push(i);
  }

  // if ((endPage <= lastPage && pages.length > 1)) {
  //   pages.push(nextPageText);
  // }

  if (endPage !== lastPage) {
    pages.push(lastPageText);
  }

  return pages;
};

export const capitalize = (str) =>
  (str.charAt(0).toUpperCase() + str.slice(1)).replace(/-/g, " ");

export const routesSlicer = ({ routes, columns = 3, rows }) => {
  const routesCollection = [];
  routes.map((route) => {
    if (route.children) {
      return route.children.map((item) => {
        if (item.children) {
          return routesCollection.push(...item.children);
        }
        return routesCollection.push(item);
      });
    }
    return routesCollection.push(route);
  });

  const totalRoutes = routesCollection.length;
  const calculatedRows = rows || Math.ceil(totalRoutes / columns);
  const routesChunks = [];
  for (let i = 0; i < totalRoutes; i += calculatedRows) {
    routesChunks.push(routesCollection.slice(i, i + calculatedRows));
  }
  return routesChunks;
};

export const getPageName = (pageName) => {
  return window.location.pathname.split("/").slice(-1)[0] === pageName;
};

export const copyToClipBoard = (textFieldRef) => {
  const textField = textFieldRef.current;
  textField.focus();
  textField.select();
  document.execCommand("copy");
};

export const errorFormatter = (errors, t) => {
  // console.log("errors.graphQLErrors:", errors.graphQLErrors);
  // console.log("errors.networkError", errors.networkError);

  //console.log("errors::", errors)

  //Rename oms service error
  if (errors?.message?.startsWith("Service Errors")) {
    return errors.message.replace("Service Errors", "OMS Error: ");
  }

  if (errors?.graphQLErrors?.length > 0) {
    return errors.message + " (GQL Error)";
  }

  if (errors?.message) {
    return errors.message;
  }

  if (typeof errors === "string" || errors instanceof String) {
    return errors;
  }

  return "";
};

export const getFileSizeWithUnit = (size) => {
  if (size < 1024) {
    return [size, "Byte"];
  } else if (size < 1024 * 1024) {
    return [(size / 1024).toFixed(2), "KB"];
  } else {
    return [(size / (1024 * 1024)).toFixed(2), "MB"];
  }
};

export const removeProps = (obj, keys) => {
  if (Array.isArray(obj)) {
    obj.forEach(function (item) {
      removeProps(item, keys);
    });
  } else if (typeof obj === "object" && obj != null) {
    Object.getOwnPropertyNames(obj).forEach(function (key) {
      if (keys.indexOf(key) !== -1) delete obj[key];
      else removeProps(obj[key], keys);
    });
  }
};

export function flattenObject(obj, prefix = "") {
  const flattened = {};
  for (const key in obj) {
    const value = obj[key];
    const newKey = prefix ? `${prefix}.${key}` : key;
    if (typeof value === "object" && value !== null) {
      Object.assign(flattened, flattenObject(value, newKey));
    } else {
      flattened[newKey] = value;
    }
  }
  return flattened;
}

export const cloneObject = (obj) => {
  return lodash.cloneDeep(obj);
};

export const uniqueId = (prefix) => {
  return lodash.uniqueId(prefix);
};

export const toRelativePath = (url) => {
  url = url.replace("https://odm-dev.isdb.org", "");
  url = url.replace("http://localhost", "");
  return url;
};

const mentionRegex = /@\[([^@]*):([a-f0-9-]*)\]/;

export const parseMentions = (body) => {
  const regex = new RegExp(mentionRegex, "g");
  let m;
  let b = [];
  while ((m = regex.exec(body))) {
    b = [
      ...b,
      {
        mention: m[0],
        name: m[1],
        id: m[2],
      },
    ];
  }
  return b;
};

export const formatMentions = (body, myUserId) => {
  const mentions = parseMentions(body);
  mentions.map((m) => {
    let className = "mention" + (m.id === myUserId ? " me" : "");
    body = body.replaceAll(
      m.mention,
      `<span href="#" class="${className}"><span>${m.name}</span></span>`
    );
  });
  return body;
};

export const hasDuplicateFilename = (currentFiles, newFiles) => {
  const filenames = [];
  const newFilenames = [];

  // console.log("currentFiles:", currentFiles);
  // console.log("NewFiles:", newFiles);

  if (currentFiles) {
    currentFiles.map((fl) => filenames.push(fl.fileName));
    //console.log("oFilenames", oFilenames);
  }

  //check if the user uploaded the same file in one go
  if (newFiles) {
    newFiles.map((fl) => newFilenames.push(fl.fileName));
    //console.log("nFilenames", nFilenames);
  }

  if (filenames.length === 0) {
    return false;
  }

  //Check if filename is already uploaded
  //console.log("COMPARE old and new!");
  // console.log("filenames:", filenames);
  // console.log("newFilenames:", newFilenames);
  if (filenames.length > 0 && newFilenames.length > 0) {
    const found = filenames.some((r) => newFilenames.includes(r));
    //console.log("FOUND SAME FILENAME:", found);

    return found;
  }

  //Ok, no duplicates found
  return false;
};

//Old version
// export const calculateLastDisbursementDate = (extended, planned) => {
//   // actualLastDisbursementDate
//   // extendedLastDisbursementDate
//   // plannedLastDisbursementDate
//   let dates = [];
//   //let timeZone = "T00:00:00.000+00:00";

//   // All stored dates are in GMT+3 (Jeddah Timezone), but the user should view them in local timezone
//   // Stored date example:
//   // dateFormatted: "2024-09-02"
//   // if (actual && Date.parse(actual)) {
//   //   dates.push({
//   //     type: "actual",
//   //     dateUTC: new Date(actual + timeZone).toISOString(),
//   //     dateFormatted: actual,
//   //     timestamp: new Date(actual + timeZone).getTime(),
//   //   });
//   // }

//   if (extended && Date.parse(extended)) {
//     dates.push({
//       type: "extended",
//       //dateUTC: new Date(extended + timeZone).toISOString(),
//       dateFormatted: extended,
//       //timestamp: new Date(extended + timeZone).getTime(),
//       dateAsInt: parseInt(extended.replaceAll("-", "")),
//     });
//   }

//   if (planned && Date.parse(planned)) {
//     dates.push({
//       type: "planned",
//       //dateUTC: new Date(planned + timeZone).toISOString(),
//       dateFormatted: planned,
//       //timestamp: new Date(planned + timeZone).getTime(),
//       dateAsInt: parseInt(planned.replaceAll("-", "")),
//     });
//   }

//   //console.log("LDD dates:", dates);

//   if (dates.length > 1) {
//     let sortedDates = dates.sort((a, b) => b.dateAsInt - a.dateAsInt);
//     //console.log("DATES SORTED:", sortedDates);
//     return sortedDates[0];
//   } else if (dates.length === 1) {
//     //console.log("SINGLE DATE:", dates);
//     return dates[0];
//   } else {
//     //No date
//     //console.log("NO DATE");
//     return "-";
//   }
// };

export const getLddSuffix = (ldd, operationStatus, t) => {
  //console.log({ ldd, operationStatus });
  //return t("operation:plannedSuffix");

  if (!ldd || !operationStatus) {
    return "";
  }

  const allowedStatus = ["Signed", "Approved"];
  if (allowedStatus?.includes(operationStatus)) {
    return t("operation:plannedSuffix");
  }

  return "";
};

export const getLddLabel = (ldd, t) => {
  //return t("operation:form.extendedLastDisbursementDate");

  if (ldd?.type === "extended") {
    return t("operation:form.extendedLastDisbursementDate");
  }

  return t("operation:form.plannedLastDisbursementDate");
};

export const getOperationDatesSuffix = (actual, planned, t) => {
  //return t("operation:plannedSuffix");

  if (actual) {
    return "";
  }

  if (planned) {
    return t("operation:plannedSuffix");
  }

  return "";
};

export const calculateLastDisbursementDateRevised = (extended, planned) => {
  let dates = [];

  if (extended && Date.parse(extended)) {
    dates.push({
      type: "extended",
      dateFormatted: extended,
      dateAsInt: parseInt(extended.replaceAll("-", "")),
    });

    return dates[0];
  }

  if (planned && Date.parse(planned)) {
    dates.push({
      type: "planned",
      dateFormatted: planned,
      dateAsInt: parseInt(planned.replaceAll("-", "")),
    });

    return dates[0];
  }

  //Default. We failed to get a usable date.
  return "-";
};

export const isValidLDD = (ldd) => {
  if (ldd?.dateFormatted && ldd?.dateAsInt) {
    let todayAsInt = parseInt(moment().locale("en").format("YYYYMMDD"));

    if (ldd.dateAsInt >= todayAsInt) {
      console.log("Last Disbursement date ALLOWED");
      console.log("ldd:", ldd.dateAsInt, "current date:", todayAsInt);
      return true;
    } else {
      console.log("Last Disbursement date MISSED!");
      console.log("ldd:", ldd.dateAsInt, "current date:", todayAsInt);
      return false;
    }
  }

  return true;
};

export const isValidPhoneNumber = (str) => {
  //Allow only numbers with optional the + sign in the beginning.
  let reg = /^(\+)?[0-9]+$/;
  return reg.test(str);
};

export const isValidEmail = (str) => {
  let reg =
    /[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*@(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?/;
  return reg.test(str);
};

//used in project app

export const fixAppPath = (path) => {
  ////console.log("fixAppPath BEFORE:", path);
  const appRoot = process.env.PUBLIC_URL || "";

  ////console.log("::::::ENV::::", process?.env);
  if (
    appRoot !== "" &&
    path.startsWith(appRoot) &&
    appRoot !== "/ticket" &&
    appRoot !== "/epp" &&
    appRoot !== "/project" &&
    appRoot !== "/health" &&
    appRoot !== "/finance"
  ) {
    // console.log(
    //   "fixAppPath AFTER startsWith(appRoot):",
    //   path.slice(appRoot.length)
    // );
    return path.slice(appRoot.length);
  }

  //Gcp Fixes
  if (window?.location?.hostname !== "localhost") {
    if (
      path.startsWith("/purchase-requisition/") ||
      path.startsWith("/payment-request/") ||
      path.startsWith("/impact-report/") ||
      path.startsWith("/project-request/")
    ) {
      //console.log("Changed to /gcp" + path);
      return "/gcp" + path;
    }
  }

  // should remove the /edp or /gcp prefix from the url
  if (path.startsWith("/edp") || path.startsWith("/gcp")) {
    //console.log("fixAppPath AFTER MOD2:", path.slice(4));
    return path.slice(4);
  }

  //console.log("fixAppPath AFTER UNMODIFIED:", path);
  return path;
};

export const appClassName = (env) => {
  if (isTest(env)) {
    return "bg-warning";
  }
};

export const appTitle = (appName, env) => {
  if (isProd(env)) return appName;
  return appName + " (" + env.name + ")";
};

export const appEnvironment = (env) => {
  if (isProd(env)) {
    return false;
  }
  return " (" + env.name + ")";
};

export const findRouteForPath = (path, routes) => {
  //console.log("path::", path);
  //console.log("all routes:", routes);
  //console.log("current config:", config);
  // Filter routes based on the current app, so 2 different apps can have the same path
  for (const route of routes.filter(
    (rt) => rt.app_id === config.environment.appName
  )) {
    const match = matchPath(path, {
      path: route.to,
      exact: route.exact,
      strict: false,
    });
    if (match) {
      //console.log("matched route:", route);
      return route;
    }
    if (route.children && route.children.length > 0) {
      return findRouteForPath(path, route.children);
    }
  }
};

export const isEmptyVariable = (value) => {
  // Check for null or undefined
  if (value === false) {
    return true;
  }

  if (value == null) {
    return true;
  }

  // Check for empty string
  if (typeof value === "string" && value.trim() === "") {
    return true;
  }

  // Check for empty array
  if (Array.isArray(value) && value.length === 0) {
    return true;
  }

  // Check for empty object
  if (
    typeof value === "object" &&
    !Array.isArray(value) &&
    Object.keys(value).length === 0
  ) {
    return true;
  }

  //Default
  return false;
};

//Compare objects and get the differences
export const compareObjects = (a, b) => {
  var excludedProps = ["__typename"]; //Add more here if required
  var result = {
    different: [],
    missing_from_first: [],
    missing_from_second: [],
  };

  //eslint-disable-next-line
  _.reduce(
    a,
    function (result, value, key) {
      //exclude checks
      if (excludedProps.includes(key)) {
        return result;
      }
      if (b?.hasOwnProperty(key)) {
        //eslint-disable-next-line
        if (_.isEqual(value, b[key])) {
          return result;
        } else {
          if (typeof a[key] != typeof {} || typeof b[key] != typeof {}) {
            //dead end.
            result.different.push(key);
            return result;
          } else {
            var deeper = compareObjects(a[key], b[key]);
            result.different = result.different.concat(
              //eslint-disable-next-line
              _.map(deeper.different, (sub_path) => {
                return key + "." + sub_path;
              })
            );

            result.missing_from_second = result.missing_from_second.concat(
              //eslint-disable-next-line
              _.map(deeper.missing_from_second, (sub_path) => {
                return key + "." + sub_path;
              })
            );

            result.missing_from_first = result.missing_from_first.concat(
              //eslint-disable-next-line
              _.map(deeper.missing_from_first, (sub_path) => {
                return key + "." + sub_path;
              })
            );
            return result;
          }
        }
      } else {
        result.missing_from_second.push(key);
        return result;
      }
    },
    result
  );
  //eslint-disable-next-line
  _.reduce(
    b,
    function (result, value, key) {
      //exclude checks
      if (excludedProps.includes(key)) {
        return result;
      }
      if (a?.hasOwnProperty(key)) {
        return result;
      } else {
        result.missing_from_first.push(key);
        return result;
      }
    },
    result
  );

  return result;
};

export const getObjectPropertiesDeep = (obj, parentKey) => {
  var excludedProps = ["__typename", "project", "state"]; //Add more here if required
  var result;
  //eslint-disable-next-line
  if (_.isArray(obj)) {
    var idx = 0;
    //eslint-disable-next-line
    result = _.flatMap(obj, function (obj) {
      return getObjectPropertiesDeep(
        obj,
        (parentKey || "") + "[" + idx++ + "]"
      );
    });
    //eslint-disable-next-line
  } else if (_.isPlainObject(obj)) {
    //eslint-disable-next-line
    result = _.flatMap(_.keys(obj), function (key) {
      //eslint-disable-next-line
      return _.map(getObjectPropertiesDeep(obj[key], key), function (subkey) {
        return (parentKey ? parentKey + "." : "") + subkey;
      });
    });
  } else {
    result = [];
  }
  //eslint-disable-next-line
  return _.remove(_.concat(result, parentKey || []), function (item) {
    return !excludedProps.includes(item);
  });
};

export const fetchFromObject = (obj, prop) => {
  //property not found
  if (typeof obj === "undefined" || obj === null) return false;

  //index of next property split
  var _index = prop.indexOf(".");

  //property split found; recursive call
  if (_index > -1) {
    //get object at property (before split), pass on remainder
    return fetchFromObject(
      obj[prop.substring(0, _index)],
      prop.substr(_index + 1)
    );
  }

  //no split; get property
  return obj[prop];
};

export const getVariableType = function (obj) {
  return {}.toString
    .call(obj)
    .match(/\s([a-zA-Z]+)/)[1]
    .toLowerCase();
};

export const isCurrentAppRoute = (config, route) => {
  //Case 1. This link is served by the current app
  //console.log("config?.environment:", config?.environment);
  //console.log("route:", route);

  // if (route?.forceReload) {
  //   return false;
  // }

  if (config?.environment?.appName === route?.app_id) {
    return true;
  } else {
    //Case 2. The route is served by an app other than the current one
    return false;
  }
};

export const transformTaskUrl = (oldUrl) => {
  if (!oldUrl) {
    return "";
  }

  //Dont touch gcp urls
  if (oldUrl.startsWith("/gcp/")) {
    //console.log("starts with gcp:", oldUrl);
    return oldUrl;
  }

  //Case WA link
  if (oldUrl.startsWith("/applications/")) {
    return "/edp" + oldUrl;
  }

  //Case ACCESS REQUEST link
  if (oldUrl.startsWith("/access-requests/")) {
    return "/project" + oldUrl;
  }

  //Case PP link
  if (oldUrl.startsWith("/procurement-plan/")) {
    return "/epp" + oldUrl;
  }

  // if (oldUrl.startsWith("/epp/project/")) {
  //   //OLD     /epp/project/6426
  //   //NEW     /project/projects/80
  //   return oldUrl.replace("/epp/project/", "/project/projects/");
  // }

  if (/^\/project\/\d+$/g.test(oldUrl)) {
    //OLD     /project/6426
    //NEW     /project/projects/6426
    return oldUrl.replace("/project/", "/project/projects/");
  }

  //Case internal GCP link
  if (
    oldUrl.startsWith("/purchase-requisition/") ||
    oldUrl.startsWith("/payment-request/") ||
    oldUrl.startsWith("/impact-report/") ||
    oldUrl.startsWith("/project-request/")
  ) {
    return "/gcp" + oldUrl;
  }

  return oldUrl;
};

export const getButtonColor = (action) => {
  //console.log("ActionButton:", action);

  /*

  */
  switch (action) {
    case "DraftToProposed":
    case "DraftToSubmitted":
    case "PendingInternalApprovalToReviewed":
    case "ProposedToSubmitted":
    case "ReviewedToApproved":
    case "SubmittedToApproved":
    case "SubmittedToPendingInternalApproval":
    case "SubmittedToReviewed":
    case "PendingApprovalToApproved":
    case "ReturnedToProposed":
    case "ReturnedToReviewed":
    case "ReturnedToSubmitted":
      return "primary";

    case "ProposedToReturned":
    case "ReviewedToSubmitted":
    case "PendingApprovalToSubmitted":
    case "SubmittedToProposed":
    case "SubmittedToReturned":
      return "warning";

    case "ReviewedToRejected":
    case "ReturnedToRejected":
      return "danger";

    default:
      return "secondary";
  }
};

export const canViewOrganizationLink = (user) => {
  let internalRoles = [
    // "CountryTicketReceiver",
    // "TicketOwner",
    // "TicketingAdmin",
    "ITAdmin",
    "OrganizationsAdmin",
  ];

  if (!isIterableArray(user?.roles)) {
    return false;
  }

  //If at least one match, then its internal
  let rolesMatch = internalRoles.filter((obj) => {
    return user.roles.indexOf(obj) !== -1;
  });

  if (rolesMatch?.length > 0) {
    //console.log("Is int because:", rolesMatch);
    return true;
  }

  //Default Deny
  return false;
};

export const isInternalUser = (user) => {
  let internalRoles = [
    "CountryTicketReceiver",
    "ITAdmin",
    "ProcurementAdmin",
    "ProcurementOfficer",
    "ProjectManager",
    "ProjectTicketReceiver",
    "TicketOwner",
    "TicketingAdmin",
    "WADisbursementOfficer",
    "WAFinanceAdmin",
    "WAHubAdmin",
    "WAReporting",
    "OrganizationsAdmin",
  ];

  if (!isIterableArray(user?.roles)) {
    return false;
  }

  //If at least one match, then its internal
  let rolesMatch = internalRoles.filter((obj) => {
    return user.roles.indexOf(obj) !== -1;
  });

  if (rolesMatch?.length > 0) {
    //console.log("Is int because:", rolesMatch);
    return true;
  }

  //Default Deny
  return false;
};

export const canCollaborateUnderAccessRequest = (user) => {
  let internalRoles = [
    "ITAdmin",
    "WAHubAdmin",
    "WAFinanceAdmin",
    "ProcurementAdmin",
    "TicketingAdmin",
  ];

  if (!isIterableArray(user?.roles)) {
    return false;
  }

  //If at least one match, then its internal
  let rolesMatch = internalRoles.filter(function (obj) {
    return user.roles.indexOf(obj) !== -1;
  });

  if (rolesMatch?.length > 0) {
    //console.log("Can Colab user because:", rolesMatch);
    return true;
  }

  //Default Deny
  return false;
};

export const canOpenProjectInOms = (user, project) => {
  //Only FDO on project can view "OpenInOms" link
  //console.log("User:", user);
  //console.log("Project:", project);

  if (!user?.id || !project?.teamMembers?.length > 0) {
    return false;
  }

  //1. Has any of the roles bellow
  if (
    user.roles.includes("WAFinanceAdmin") ||
    user.roles.includes("WAReporting") ||
    user.roles.includes("ProjectManager")
  ) {
    return true;
  }

  //2. Is FDO or AFDO in the project
  // FieldDisbursementOfficer: "47",
  // AlternateFieldDisbursementOfficer: "50",
  // Extract users with roles above
  let matchFdos = project?.teamMembers.filter((tm) => {
    return tm?.roleId === "47" || tm?.roleId === "50";
  });

  //console.log("matchFdos Users:", matchFdos);

  if (matchFdos.length < 1) {
    return false;
  }

  //MAtch against current User
  let matchUser = matchFdos.filter((fdo) => {
    return fdo?.user?.id === user.id;
  });

  //console.log("Matched User:", matchUser);
  if (matchUser.length > 0) {
    return true;
  }

  //Default Deny
  return false;
};

export const canAddWAOfficialResponse = (user, entity) => {
  if (!user?.id || !entity?.applicationProject?.teamMembers?.length > 0) {
    return false;
  }

  if (!(entity?.state?.code === "Paid" || entity?.state?.code === "Approved")) {
    return false;
  }

  if (user.roles.includes("WADisbursementOfficer")) {
    return true;
  }
  return false;
};

export const isTicketOwner = (user, ticket) => {
  //console.log("isTicketOwner:", user, ticket);
  if (!ticket?.participants || !user?.id) {
    return false;
  }

  let ownerFound = ticket.participants.filter((prtc) => {
    return prtc?.roleNames.includes("Owner") && prtc?.user?.id === user.id;
  });

  //console.log("ownerFound:", ownerFound);

  if (ownerFound.length > 0) {
    return true;
  }

  //Default deny
  return false;
};

export const processAttachmentData = (files) => {
  if (!files || files?.length < 1) {
    return [];
  }

  return files?.map((file) => {
    if (file?.category) {
      return {
        id: file?.id,
        type: file?.type,
        url: file?.url,
        fileName: file?.fileName,
        fileSize: file?.fileSize,
        mediaType: file?.mediaType,
        eTag: file?.etag,
        category: file?.category,
      };
    }

    return {
      id: file?.id,
      type: file?.type,
      url: file?.url,
      fileName: file?.fileName,
      fileSize: file?.fileSize,
      mediaType: file?.mediaType,
      eTag: file?.etag,
    };
  });
};

export const getSearchQueryParams = (urlQueryString) => {
  // const { filterKey } = props?.history?.location?.state;
  // const search = props.history?.location.search; // could be '?state=InProgress'
  // const params = new URLSearchParams(search);
  // const state = params.get("state"); // InProgress

  var keyValuePairs = urlQueryString.split(/[&?]/g);
  var params = {};
  for (var i = 0, n = keyValuePairs.length; i < n; ++i) {
    var m = keyValuePairs[i].match(/^([^=]+)(?:=([\s\S]*))?/);
    if (m) {
      var key = decodeURIComponent(m[1]);
      (params[key] || (params[key] = [])).push(decodeURIComponent(m[2]));
    }
  }

  if (params?.state) {
    return params?.state[0];
  }

  // return state;
};

export const isHealthInternalUser = (user) => {
  const healthInternalRoles = [
    "MedicalServicesDirector",
    "MedicalInsuranceOfficer",
    "MedicalInsuranceDoctor",
    "MedicalInsuranceDesk",
    "ChiefAdministrativeOfficer",
    "HRPensionersOfficer",
  ];
  const foundInternalRoles = user?.roles?.filter((role) => {
    if (healthInternalRoles?.includes(role)) {
      return true;
    }
    return false;
  });
  //console.log('found Internal health Roles', foundInternalRoles);
  return !!foundInternalRoles?.length;
};

export const appLanguages = {
  admin: ["en"],
  dashboard: ["en", "fr", "ar"],
  edp: ["en", "fr", "ar"],
  epp: ["en", "fr", "ar"],
  gcp: ["en", "fr", "ar"],
  health: ["en", "ar"], //No French
  inbox: ["en", "fr", "ar"],
  project: ["en", "fr", "ar"],
  ticket: ["en", "fr", "ar"],
  user: ["en", "fr", "ar"],
  welcome: ["en", "fr", "ar"],
  organization: ["en", "fr", "ar"],
  finance: ["en", "fr", "ar"],
};
export const getTaskProcessFilterOptions = (
  userApps,
  t,
  allowAllOptions = false
) => {
  //console.log("userApps::", userApps);
  let availableOptions = [
    {
      label: t("task:processFilterOptions.WithdrawalApplication"),
      value: "WithdrawalApplication",
      app: "edp",
    },
    {
      label: t("task:processFilterOptions.AccessRequest"),
      value: "AccessRequest",
      app: "project",
    },
    {
      label: t("task:processFilterOptions.ProcurementPlan"),
      value: "ProcurementPlan",
      app: "epp",
    },
    {
      label: t("task:processFilterOptions.ProcurementExecutionRequest"),
      value: "ProcurementExecutionRequest",
      app: "epp",
    },
    {
      label: t("task:processFilterOptions.GeneralProcurementNoticeRequest"),
      value: "GeneralProcurementNoticeRequest",
      app: "epp",
    },
    {
      label: t("task:processFilterOptions.Ticket"),
      value: "Ticket",
      app: "ticket",
    },
    // {
    //   label: t("task:processFilterOptions.Claim"),
    //   value: "MedicalClaim",
    //   app: "health",
    // },
    {
      label: t("task:processFilterOptions.FinancialAuditReport"),
      value: "FinancialAuditReport",
      app: "finance",
    },
  ];

  if (allowAllOptions === true) {
    return availableOptions;
  }

  const isHealthSubApp = !!window.location.pathname.startsWith("/health");

  // This filtering produces a list of process filter options that the user should see,
  // depending on which sub-application they're currently using.
  //
  // If the user is in the "health" sub-app (URL path starts with "/health"):
  //   - Only options belonging to the "health" app are included, but only if that app is in the user's apps (or "project" for back-compat).
  // If the user is NOT in the "health" sub-app:
  //   - All options EXCEPT for "health" app are included, so long as the app is in the user's apps (or "project").
  let userOptions = availableOptions.filter((opt) => {
    if (isHealthSubApp) {
      // Only show "health" options, if the user has access
      return (
        [...userApps, "project"].indexOf(opt?.app) >= 0 && opt?.app === "health"
      );
    } else {
      // Show all options except "health", if the user has access
      return (
        [...userApps, "project"].indexOf(opt?.app) >= 0 && opt?.app !== "health"
      );
    }
  });

  //console.log("FINAL userOptions:", userOptions);

  return userOptions;
};

export const getProjectStateFilterOptions = (t) => {
  return [
    { label: t("project:state.Appraised"), value: "Appraised" },
    { label: t("project:state.Approved"), value: "Approved" },
    { label: t("project:state.Cancelled"), value: "Cancelled" },
    { label: t("project:state.Completed"), value: "Completed" },
    { label: t("project:state.ConceptCleared"), value: "ConceptCleared" },
    {
      label: t("project:state.ConditionalApproval"),
      value: "ConditionalApproval",
    },
    {
      label: t("project:state.FinancingAgreementEffective"),
      value: "FinancingAgreementEffective",
    },
    {
      label: t("project:state.FinancingAgreementSigned"),
      value: "FinancingAgreementSigned",
    },
    { label: t("project:state.Frozen"), value: "Frozen" },
    { label: t("project:state.OnDisbursement"), value: "OnDisbursement" },
    { label: t("project:state.PCDCompleted"), value: "PCDCompleted" },
    { label: t("project:state.Pipeline"), value: "Pipeline" },
    { label: t("project:state.Postponed"), value: "Postponed" },
    { label: t("project:state.Prepared"), value: "Prepared" },
    {
      label: t("project:state.SubmittedForApproval"),
      value: "SubmittedForApproval",
    },
    { label: t("project:state.Terminated"), value: "Terminated" },
  ];
};

export const getDisabledProjectStates = () => {
  return [
    "Created",
    "Dropped",
    "Inactive",
    "Rejected",
    "RepaymentStopped",
    "Suspended",
    "UnderRepayment",
  ];
};

//This is a comment

export const getEntityStatesByType = (entityType) => {
  return entityStates.filter((s) => !entityType || entityType === s.type);
};
export const removeInvalidChars = (val) => {
  return !val
    ? val
    : val.replace(/\[|\]|\{|\}|\(|\)|\*|\+|\$|\||\&|\?|\^/g, (matched) => "");
};

// Explanation: Inside a regex character class [], most special characters don't need escaping. Only ], \, and sometimes ^ and - need escaping.
// The pattern had unnecessary escapes for {, }, (, ), /, +, !, #, $, %, &, and *. Removed those escapes,
// keeping only the necessary ones (] and \\ for the backslash). The regex now correctly matches the same characters without the linter warning.
export const removeInvalidSearchChars = (val) => {
  // Removes the following characters: [, ], (, ), {, }, \, /, +, !, #, $, %, ^, &, *,
  let cleanedVal = "";
  cleanedVal = val?.replace(/[[\](){}/\\+!#$%^&*]/g, "") || "";
  return cleanedVal;
};

const entityStates = [
  //Ticket
  { value: "Draft", type: "Ticket", icon: faSquare },
  { value: "Submitted", type: "Ticket", icon: fasStream },
  { value: "Deleted", type: "Ticket", icon: faSquare },
  { value: "InProgress", type: "Ticket", icon: faPlayCircle },
  { value: "Returned", type: "Ticket", icon: faArrowAltCircleLeft },
  { value: "Resolved", type: "Ticket", icon: fasCheckCircle },
  { value: "Cancelled", type: "Ticket", icon: faTimesCircle },
  { value: "Closed", type: "Ticket", icon: faHandshake },
  //access request
  { value: "UnderReview", type: "AccessRequest", icon: faEye },
  { value: "Pending", type: "AccessRequest", icon: fasDotCircle },
  { value: "Rejected", type: "AccessRequest", icon: faTimesCircle },
  { value: "Approved", type: "AccessRequest", icon: fasCheckCircle },
  { value: "Registered", type: "AccessRequest", icon: faSquare },
  //Procurement Plan
  { value: "Draft", type: "ProcurementPlan", icon: faSquare },
  { value: "Prepared", type: "ProcurementPlan", icon: fasDotCircle },
  { value: "OnReview", type: "ProcurementPlan", icon: fasStream },
  { value: "ModificationRequested", type: "ProcurementPlan", icon: faSquare },
  { value: "Active", type: "ProcurementPlan", icon: fasCheckCircle },
  { value: "Returned", type: "ProcurementPlan", icon: faArrowAltCircleLeft },
  { value: "Submitted", type: "ProcurementPlan", icon: fasStream },
  { value: "ReadyForApproval", type: "ProcurementPlan", icon: faArrowsAlt },
  { value: "Deferred", type: "ProcurementPlan", icon: faPauseCircle },
  { value: "OnRevision", type: "ProcurementPlan", icon: faArrowAltCircleUp },
  //Withdrawal Application
  { value: "Draft", type: "WithdrawalApplication", icon: faSquare },
  { value: "Deleted", type: "WithdrawalApplication", icon: faSquare },
  { value: "Prepared", type: "WithdrawalApplication", icon: fasDotCircle },
  { value: "Cancelled", type: "WithdrawalApplication", icon: faTimesCircle },
  {
    value: "ModificationRequested",
    type: "WithdrawalApplication",
    icon: faSquare,
  },
  { value: "Submitted", type: "WithdrawalApplication", icon: fasStream },
  { value: "UnderReview", type: "WithdrawalApplication", icon: faEye },
  { value: "InProgress", type: "WithdrawalApplication", icon: faPlayCircle },
  {
    value: "Returned",
    type: "WithdrawalApplication",
    icon: faArrowAltCircleLeft,
  },
  { value: "Rejected", type: "WithdrawalApplication", icon: faTimesCircle },
  { value: "Approved", type: "WithdrawalApplication", icon: fasCheckCircle },
  {
    value: "CancelledByBank",
    type: "WithdrawalApplication",
    icon: faTimesCircle,
  },
  {
    value: "RejectedByBank",
    type: "WithdrawalApplication",
    icon: faTimesCircle,
  },
  //ProcurementExecution
  {
    value: "Cancelled",
    type: "ProcurementExecutionRequest",
    icon: fasThumbsUp,
  },
  {
    value: "InProgress",
    type: "ProcurementExecutionRequest",
    icon: fasThumbsUp,
  },
  { value: "OnReview", type: "ProcurementExecutionRequest", icon: fasThumbsUp },
  { value: "Created", type: "ProcurementExecutionRequest", icon: fasThumbsUp },
  { value: "Active", type: "ProcurementExecutionRequest", icon: fasThumbsUp },
  {
    value: "ReviewApproved",
    type: "ProcurementExecutionRequest",
    icon: fasThumbsUp,
  },
  {
    value: "Completed",
    type: "ProcurementExecutionRequest",
    icon: fasThumbsUp,
  },
  {
    value: "OnRevision",
    type: "ProcurementExecutionRequest",
    icon: fasThumbsUp,
  },
  {
    value: "ReviewNotApproved",
    type: "ProcurementExecutionRequest",
    icon: fasThumbsUp,
  },
  {
    value: "NonComplianceDeclared",
    type: "ProcurementExecutionRequest",
    icon: fasThumbsUp,
  },
  {
    value: "ReviewRequiresResubmission",
    type: "ProcurementExecutionRequest",
    icon: fasThumbsUp,
  },
  { value: "Returned", type: "ProcurementExecutionRequest", icon: fasThumbsUp },
  {
    value: "ValidComplaint",
    type: "ProcurementExecutionRequest",
    icon: fasThumbsUp,
  },
  {
    value: "Terminated",
    type: "ProcurementExecutionRequest",
    icon: fasThumbsUp,
  },
  // {
  //   value: "CancelledBySubmitter",
  //   type: "ProcurementExecutionRequest",
  //   icon: fasThumbsUp,
  // },
  { value: "Draft", type: "ProcurementExecutionRequest", icon: fasThumbsUp },
  {
    value: "Submitted",
    type: "ProcurementExecutionRequest",
    icon: fasThumbsUp,
  },
  { value: "Prepared", type: "ProcurementExecutionRequest", icon: fasThumbsUp },
  {
    value: "ModificationRequested",
    type: "ProcurementExecutionRequest",
    icon: fasThumbsUp,
  },
  // {
  //   value: "FirmDeclined",
  //   type: "ProcurementExecutionRequest",
  //   icon: fasThumbsUp,
  // },
  {
    value: "NegotiationUnsuccessful",
    type: "ProcurementExecutionRequest",
    icon: fasThumbsUp,
  },
  //Claim
  { value: "Draft", type: "MedicalClaim", icon: faSquare },
  { value: "Deleted", type: "MedicalClaim", icon: faSquare },
  { value: "Pending", type: "MedicalClaim", icon: fasDotCircle },
  { value: "Dropped", type: "MedicalClaim", icon: faTimesCircle },
  { value: "Rejected", type: "MedicalClaim", icon: faTimesCircle },
  { value: "DoctorApproved", type: "MedicalClaim", icon: faUserNurse },
  {
    value: "PendingResubmission",
    type: "MedicalClaim",
    icon: faArrowAltCircleLeft,
  },
  { value: "ReconcilingWithDoctor", type: "MedicalClaim", icon: fasStream },
  {
    value: "ReconcilingWithInsuranceDesk",
    type: "MedicalClaim",
    icon: fasStream,
  },
  { value: "Verified", type: "MedicalClaim", icon: faCalendarCheck },
  {
    value: "ReconcilingForVerification",
    type: "MedicalClaim",
    icon: fasStream,
  },
  {
    value: "InsuranceOfficerApproved",
    type: "MedicalClaim",
    icon: faLaptopHouse,
  },
  { value: "Approved", type: "MedicalClaim", icon: fasCheckCircle },
  {
    value: "ReconcilingWithInsuranceOfficer",
    type: "MedicalClaim",
    icon: fasStream,
  },
  { value: "ReconcilingWithDirector", type: "MedicalClaim", icon: fasStream },
  { value: "DirectorApproved", type: "MedicalClaim", icon: faUserCheck },
  {
    value: "ReconcilingWithChiefOfficer",
    type: "MedicalClaim",
    icon: fasStream,
  },
  { value: "PostedToPayroll", type: "MedicalClaim", icon: faMoneyBillAlt },

  // "FinancialAuditReport"
  {
    value: "Draft",
    type: "FinancialAuditReport",
  },
  {
    value: "Cancelled",
    type: "FinancialAuditReport",
  },
  {
    value: "Submitted",
    type: "FinancialAuditReport",
  },
  {
    value: "InProgress",
    type: "FinancialAuditReport",
  },
  {
    value: "Returned",
    type: "FinancialAuditReport",
  },
  {
    value: "Approved",
    type: "FinancialAuditReport",
  },
  {
    value: "Rejected",
    type: "FinancialAuditReport",
  },
  {
    value: "Terminated",
    type: "FinancialAuditReport",
  },
];

export const formatBytes = (bytes, decimals = 2) => {
  if (bytes === 0) return "0 Bytes";

  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ["Bytes", "KB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"];

  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + " " + sizes[i];
};

export const formatToBytes = (size, type) => {
  const types = ["B", "KB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"];

  const key = types.indexOf(type.toUpperCase());

  if (typeof key !== "boolean") {
    return size * 1024 ** key;
  }
  return "invalid type: type must be GB/KB/MB etc.";
};

export const dataURLtoFileObject = (dataUrl, filename) => {
  console.log("generating file:", filename);
  console.log("dataUrl:", dataUrl);
  var arr = dataUrl.split(","),
    mime = arr[0].match(/:(.*?);/)[1],
    bstr = atob(arr[1]),
    n = bstr.length,
    u8arr = new Uint8Array(n);
  while (n--) {
    u8arr[n] = bstr.charCodeAt(n);
  }
  return new File([u8arr], filename, { type: mime });
};

export const getUserAvatarUrl = (avatarUrl) => {
  if (!avatarUrl) {
    console.log("Error! no avatar url provided.");
    return false;
  }

  return avatarUrl;
};

export const stringTruncate = (str, n, useWordBoundary) => {
  if (str.length <= n) {
    return str;
  }
  const subString = str.slice(0, n - 1); // the original check
  return (
    (useWordBoundary
      ? subString.slice(0, subString.lastIndexOf(" "))
      : subString) + "..."
  );
};

export const isGcpApp = (env) => {
  // This function is used to detect if we are on gcp admin or odm admin app
  // In order to show the correct home url in the breadcrumbs

  //console.log("config:::", env);

  if (!env?.graphQLServerUri) {
    return false;
  }

  if (env.graphQLServerUri.includes("/gcp/")) {
    return true;
  }

  return false;
};

export const makeId = (length) => {
  var result = "";
  var characters =
    "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
  var charactersLength = characters.length;
  for (var i = 0; i < length; i++) {
    result += characters.charAt(Math.floor(Math.random() * charactersLength));
  }
  return result;
};

export const transformRoute = (route) => {
  //console.log("transformRoute window?.location:::::::", window?.location);
  let newRoute = cloneObject(route);
  //return route;

  if (route?.app_id !== "health") {
    newRoute["to"] = `/${route?.app_id}${route?.to}`;
    //console.log("1 newRoute", newRoute);
    return newRoute;
  }

  if (
    window.location.hostname === "odm-dev.isdb.org" ||
    window.location.hostname === "odm-qa.isdb.org" ||
    window.location.hostname === "odm.isdb.org" ||
    window.location.hostname === "localhost"
  ) {
    newRoute["to"] = `/${route?.app_id}${route?.to}`;
    //console.log("2 newRoute", newRoute);
    return newRoute;
  }

  //default
  return route;
};

export const getDashboardUrl = () => {
  // console.log(
  //   "getDashboardUrl: config, window?.location",
  //   config,
  //   window?.location
  // );

  if (
    window.location.hostname === "odm-dev.isdb.org" ||
    window.location.hostname === "odm-qa.isdb.org" ||
    window.location.hostname === "odm.isdb.org" ||
    window.location.hostname === "localhost"
  ) {
    return `/health/`;
  }

  return "/";
};

export const getAppName = (t) => {
  let appName = "";
  const host = window.location.host.toLowerCase();
  switch (host) {
    case "gcp.isdb.org":
    case "gcp-dev.isdb.org":
    case "gcp-qa.isdb.org":
      appName = "appNameGCP";
      break;
    case "health.isdb.org":
    case "health-dev.isdb.org":
    case "health-qa.isdb.org":
      appName = "healthAppName";
      break;
    default:
      appName = "appName";
      break;
  }

  return t(`common:${appName}`);
};

export const transformFilesUrl = (url) => {
  let origin = "";
  const host = window.location.host.toLowerCase();

  if (!url || host?.includes("https://odm")) {
    return url;
  }

  switch (host) {
    case "gcp.isdb.org":
    case "gcp-dev.isdb.org":
    case "gcp-qa.isdb.org":
      origin = "https://gcp";
      break;
    case "health.isdb.org":
    case "health-dev.isdb.org":
    case "health-qa.isdb.org":
      origin = "https://health";
      break;
    default:
      origin = "https://odm";
      break;
  }
  //console.log('transformFilesUrl : ', url.replace('https://odm', origin));
  return url.replace("https://odm", origin);
};

export const sortByDateProperty = (array, property, order) => {
  if (!array || !Array.isArray(array)) {
    return array;
  }
  //console.log("array to sort", array);
  let arrayData = [...array];
  arrayData.sort(function (a, b) {
    // Turn your strings into dates, and then subtract them
    // to get a value that is either negative, positive, or zero.
    return new Date(b[property]) - new Date(a[property]);
  });
  if (order === "asc") {
    arrayData.reverse();
  }
  //console.log("array sorted :", arrayData);
  return arrayData;
};

const getRandomLetters = (length = 1) =>
  Array(length)
    .fill()
    .map((e) => String.fromCharCode(Math.floor(Math.random() * 26) + 65))
    .join("");
const getRandomDigits = (length = 1) =>
  Array(length)
    .fill()
    .map((e) => Math.floor(Math.random() * 10))
    .join("");
export const generateUniqueID = (length) => {
  const len = length ?? 8;
  let digLen, letLen;
  digLen = letLen = len / 2;
  if (len % 2 !== 0) {
    digLen = Math.floor(digLen);
    letLen = Math.ceil(letLen);
  }
  let id = getRandomLetters(letLen) + getRandomDigits(digLen);
  return id.toLowerCase();
};

export const generateUniqueFileName = (filename) => {
  if (!filename) {
    return "";
  }
  const ext = filename.split(".").pop();
  const name = filename.substr(0, filename.lastIndexOf("."));
  return `${name}-${generateUniqueID(8)}.${ext}`;
};

export const revertUniqueFileName = (filename) => {
  //console.log("revertUniqueFileName filename", filename);
  if (!filename) {
    return "";
  }
  let uniqueIdIndex = filename?.lastIndexOf("-");
  const id = filename.slice(uniqueIdIndex, filename.lastIndexOf("."));
  if (!id || uniqueIdIndex === -1 || id?.length !== 9) {
    // id + ( - )
    return filename;
  }
  return filename.replace(id, "");
};

export const generateDateUniqueFileName = (filename) => {
  if (!filename) {
    return "";
  }
  const ext = filename.split(".").pop();
  const name = filename.substr(0, filename.lastIndexOf("."));
  const randomString = Math.floor(Math.random() * 9000000000) + 1000000000;
  return `${moment().locale("en").format("YYYYMMDD_HHmmSS")}_${name}.${ext}`;
};

export const skipNotUploadedFiles = (uploading, t) => {
  if (uploading) {
    const skipUploadingFiles = window.confirm(t("common:skipNotUploadedFiles"));
    return skipUploadingFiles;
  }
};

export const isUnauthorized = (errors) => {
  if (errors?.message?.match(/Not authorized/gi)) {
    return true;
  }
  return false;
};

// export const detectDeviceType = () => {
//   return Detectizr && Detectizr.device.type;
// };

// export const detectDeviceOrientation = () => {
//   return Detectizr && Detectizr.device.orientation;
// };

// export const isMobile = () => {
//   return detectDeviceType() === "mobile";
// };

//Deprecated use BrowserCompatibilityChecker instead
// export const checkBrowserVersion = () => {
//   const userAgent = navigator.userAgent;

//   // Define the supported browser versions
//   const supportedVersions = {
//     Chrome: getLastTenVersions("112"),
//     Firefox: getLastTenVersions("112"),
//     Edge: getLastTenVersions("113"),
//     Safari: getLastTenVersions("16"), // For macOS Safari
//     // Safari on iOS/iPadOS version checking is not supported
//   };

//   const browserSupported = ["chrome", "firefox", "edge-chromium", "safari"];

//   if (!browserSupported.includes(getBrowserDetails().name)) {
//     return "The application will be not be fully supported with the current browser, please try to use following browser for optimal performance. (Chrome, Firefox, Edge, Safari)";
//   }

//   // Extract the browser name and version from the user agent
//   let browserName, browserVersion;
//   if (userAgent.indexOf("Chrome") > -1) {
//     browserName = "Chrome";
//     browserVersion = parseInt(userAgent.match(/Chrome\/(\d+)/)[1]);
//   } else if (userAgent.indexOf("Firefox") > -1) {
//     browserName = "Firefox";
//     browserVersion = parseInt(userAgent.match(/Firefox\/(\d+)/)[1]);
//   } else if (userAgent.indexOf("Edge") > -1) {
//     browserName = "Edge";
//     browserVersion = parseInt(userAgent.match(/Edg\/(\d+)/)[1]);
//   } else if (userAgent.indexOf("Safari") > -1) {
//     browserName = "Safari";
//     browserVersion = parseInt(userAgent.match(/Version\/(\d+)/)[1]);
//   }

//   // Check if the current browser version is included in the supported versions

//   if (
//     supportedVersions[browserName] &&
//     !supportedVersions[browserName].includes(browserVersion)
//   ) {
//     // Display a popup alert to the user
//     return "Your browser is outdated. Please consider updating to the latest version for optimal performance.";
//   }
//   return "";
// };

//Deprecated use BrowserCompatibilityChecker instead
// export const getLastTenVersions = (latestVersion) => {
//   const majorVersion = parseInt(latestVersion.split(".")[0]);
//   const versions = [];
//   for (let i = majorVersion; i > majorVersion - 10; i--) {
//     // versions.push(`${i}.0.0`);
//     versions.push(i);
//   }
//   // console.log("version", versions);
//   return versions;
// };

//Deprecated use BrowserCompatibilityChecker instead
// export const getBrowserDetails = () => {
//   const browser = detect();
//   return browser;
// };

export const getFileIconFromUrl = (url) => {
  let fileExt = "";
  if (url) {
    let split = url.split(".");

    try {
      fileExt = split?.slice(-1)[0]?.toLowerCase();
    } catch (error) {
      console.log("Error getting file extension:", error);
      fileExt = "";
    }
  }

  switch (fileExt) {
    case "pdf":
      return faFilePdf;

    case "png":
    case "svg":
    case "jpeg":
    case "jpg":
    case "tiff":
      return faFileImage;

    case "avi":
    case "mov":
    case "wmv":
      return faFileVideo;

    case "xlsx":
    case "xls":
    case "xltx":
      return faFileExcel;

    case "doc":
    case "docx":
    case "csv":
      return faFileWord;

    case "deleted":
      return faTrash;

    default:
      return faFile;
  }
};

// export const canViewActiveTask = (user, app) => {
//   if (!user?.roles?.length) {
//     return false;
//   }
//   if (!app) {
//     return false;
//   }
//   if (user?.roles?.includes("ITAdmin")) {
//     return true;
//   }

//   const allowedPermissions = {
//     edp: [
//       "WAHubAdmin",
//       "WADisbursementOfficer",
//       "WAFinanceAdmin",
//       "WAReporting",
//     ],
//     epp: ["ProcurementAdmin", "ProcurementOfficer"],
//     ticket: [
//       "TicketingAdmin",
//       "CountryTicketReceiver",
//       "ProjectTicketReceiver",
//       "TicketOwner",
//     ],
//     project: ["ProjectManager"],
//   };

//   const appPermissions = allowedPermissions[app];
//   const isAllowed = user.roles.some((r) => appPermissions.includes(r));
//   //console.log("canViewActiveTask", { roles: user.roles, app, isAllowed });
//   return isAllowed;
// };

// export const canAddActiveTaskOwner = (user, app) => {
//   if (!user?.roles?.length) {
//     return false;
//   }
//   if (!app) {
//     return false;
//   }
//   if (user?.roles?.includes("ITAdmin")) {
//     return true;
//   }
//   const allowedPermissions = {
//     edp: ["WAFinanceAdmin"],
//     epp: ["ProcurementAdmin"],
//     ticket: ["TicketingAdmin"],
//   };

//   const appPermissions = allowedPermissions[app];
//   const isAllowed = user.roles.some((r) => appPermissions.includes(r));
//   //console.log("canAddActiveTaskOwner", { roles: user.roles, app, isAllowed });
//   return isAllowed;
// };

export const getOmsFileUrl = (doc, docDefinition) => {
  let hostUrl =
    window?.location?.hostname === "localhost"
      ? "https://clientgateway-dev.isdb.org"
      : window?.location?.origin;

  let omsFileUrl = "";

  omsFileUrl = `${hostUrl}/entityFileUpload/${docDefinition?.id}/documents/${doc?.id}/${doc?.fileName}`;
  //console.log("omsFileUrl:", omsFileUrl);
  return omsFileUrl;
};

export const hasInvalidCharacters = (value) => {
  const invalidCharacters = ["\\", "+", "{", "(", ")"];
  return invalidCharacters.some((char) => value.includes(char));
};

export const omsAvailabilityRequired = (value) => {
  const host = window.location.host.toLowerCase();
  switch (host) {
    case "gcp.isdb.org":
    case "gcp-dev.isdb.org":
    case "gcp-qa.isdb.org":
    case "health.isdb.org":
    case "health-dev.isdb.org":
    case "health-qa.isdb.org":
      return false;
    case "clientgateway-dev.isdb.org":
    case "clientgateway-qa.isdb.org":
    case "clientgateway.isdb.org":
      return true;
    case "localhost:3000":
      const requiredApps = ["gcp", "health"];
      const app =
        window?.location?.pathname?.split("/")?.[1]?.toLowerCase() || "";
      if (requiredApps.includes(app)) {
        return false;
      } else {
        return true;
      }
    default:
      return true;
  }
};

export const scrollToCard = (element, margin = 100) => {
  if (!element) {
    return false;
  }
  let dimensions = element.getBoundingClientRect();
  //console.log("scroling top:", dimensions.top - margin);
  window.scrollTo(window.scrollX, dimensions.top - margin, "smooth");
};

export const getEHealthCardsUrl = (beneficiaryId) => {
  const host = window.location.host.toLowerCase();
  const isDEV = host === "health-dev.isdb.org" || host === "localhost:3000";
  const isQA = host === "health-qa.isdb.org";
  const isPRD = host === "health.isdb.org";
  let baseUrl = isDEV
    ? "https://extranetdev.isdb.org/oms/rest/medical"
    : isQA
      ? "https://extranetqas.isdb.org/oms/rest/medical"
      : "https://extranet.isdb.org/oms/rest/medical";

  return `${baseUrl}/${beneficiaryId}`;
};

export function getMimeTypeFromArrayBuffer(arrayBuffer) {
  const uint8arr = new Uint8Array(arrayBuffer);

  const len = 4;
  if (uint8arr.length >= len) {
    let signatureArr = new Array(len);
    for (let i = 0; i < len; i++)
      signatureArr[i] = new Uint8Array(arrayBuffer)[i].toString(16);
    const signature = signatureArr.join("").toUpperCase();
    //console.log("signature", signature);
    switch (signature) {
      case "425047FB":
        return "image/bpg";
      case "89504E47":
        return "image/png";
      case "47494638":
        return "image/gif";
      case "25504446":
        return "application/pdf";
      case "FFD8FFEE":
      case "FFD8FFDB":
      case "FFD8FFE0":
      case "FFD8FFE1":
        return "image/jpeg";
      case "52494646":
        return "image/webp";
      case "FEFF":
      case "FFFE":
      case "EFBBBF":
      case "FFFE0000":
      case "0000FEFF":
        return "text/plain";
      default:
        return null;
    }
  }
  return null;
}

export function getMimeTypeFromExt(ext) {
  switch (ext?.toLowerCase()) {
    case "bpg":
      return "image/bpg";
    case "png":
      return "image/png";
    case "gif":
      return "image/gif";
    case "pdf":
      return "application/pdf";
    case "jpg":
    case "jpeg":
      return "image/jpeg";
    case "webp":
      return "image/webp";
    case "txt":
      return "text/plain";
    default:
      return null;
  }
}

export const getCountryName = (countryCode, countryName, t) => {
  if (countryCode === "999") {
    return t("common:countryNotAssigned");
  }

  if (countryCode === "RG") {
    return t("common:regionalCountry");
  }

  return countryName;
};

export const getCalendarPopperPlacement = (isIpad) => {
  if (isIpad) {
    return "bottom";
  }

  return "left";
};

//The function is used to fetch all the results
export const fetchAllResults = (
  client,
  queryName,
  query,
  variables,
  chunkSize = 30
) => {
  return new Promise(async (resolve, reject) => {
    try {
      if (!client) {
        console.log("Apollo Client not initialized");
        reject("Apollo Client not initialized");
      }

      let vars = { ...variables };

      if (vars) {
        vars["range"] = {
          page: 0,
          size: 1,
        };
      }

      const firstPage = await asynchronousServiceCall(client, query, vars);

      const pageInfo = firstPage?.[queryName]?.pageInfo;
      let pageCount = 0;
      let promises = [];
      let result = [];
      let totalPages = Math.ceil(pageInfo?.totalCount / chunkSize);

      while (totalPages > pageCount) {
        vars["range"] = {
          page: pageCount,
          size: chunkSize,
        };

        let promiseResponse = await asynchronousServiceCall(
          client,
          query,
          vars
        );

        promises.push(promiseResponse);
        pageCount = pageCount + 1;
      }

      const responseData = await Promise.all(promises);
      //console.log("responseData::", responseData);
      responseData.forEach((data) => {
        result = [...result, ...data?.[queryName]?.nodes];
      });

      resolve(result);
    } catch (error) {
      reject(error);
    }
  });
};

// Utility function to sanitize file names
export const sanitizeFileName = (fileName) => {
  // Including `%` and other invalid characters
  const invalidCharacters = /[%<>:"/\\|?*\s]+/g;
  return fileName.replace(invalidCharacters, "_");
};

// not allowed special characters in file names
export const INVALID_FILENAME_CHARACTERS = [";"];

export const validateFileNameForUpload = (fileName) => {
  if (!fileName) {
    return true;
  }
  const invalidChars = INVALID_FILENAME_CHARACTERS.filter((char) =>
    fileName.includes(char)
  );
  if (invalidChars.length === 0) {
    return true;
  }

  toast.error(
    i18n.t("common:invalidFileNameCharactersError", {
      specialCharacters: invalidChars.join(" "),
    }),
    {
      autoClose: false,
    }
  );

  return false;
};

export const getProjectStateColor = (state) => {
  switch (state) {
    case "FinancingAgreementEffective":
    case "OnDisbursement":
      return "success";
    case "Inactive":
    case "UnderReview":
      return "warning";
    case "Approved":
    case "DisbursementCompleted":
    case "OperationCompleted":
    case "UnderRepayment":
    case "Prepared":
    case "SubmittedForApproval":
    case "Pipeline":
    case "Created":
    case "ConditionalApproval":
    case "ConceptCleared":
    case "PostEvaluation":
    case "RepaymentStopped":
    case "PipelineB":
    case "Appraised":
    case "FinancingAgreementSigned":
    case "Repaid":
    case "PCDCompleted":
    case "Pending":
      return "primary";
    case "Postponed":
    case "Suspended":
    case "Frozen":
    case "Dropped":
    case "Cancelled":
    case "Rejected":
    case "Terminated":
      return "danger";
    default:
      return "";
  }
};

export const getProjectStateIcon = (state) => {
  switch (state) {
    case "Created":
    case "PostEvaluation":
      return faSquare;
    case "Approved":
    case "DisbursementCompleted":
    case "OperationCompleted":
    case "Terminated":
    case "Repaid":
    case "PCDCompleted":
      return faCheckCircle;
    case "Completed":
      return fasCheckCircle;
    case "Dropped":
    case "Cancelled":
    case "Rejected":
      return faTimesCircle;
    case "Inactive":
    case "RepaymentStopped":
      return faBan;
    case "Prepared":
    case "SubmittedForApproval":
    case "Pipeline":
    case "ConditionalApproval":
    case "ConceptCleared":
    case "PipelineB":
    case "Appraised":
    case "Pending":
      return fasDotCircle;
    case "OnDisbursement":
    case "UnderRepayment":
      return faPlayCircle;
    case "FinancingAgreementSigned":
    case "FinancingAgreementEffective":
      return faHandshake;
    case "Postponed":
    case "Suspended":
    case "Frozen":
      return faPauseCircle;
    case "UnderReview":
      return faEye;
    default:
      return faQuestionCircle;
  }
};

// Function to recursively remove all null properties from an object
export function removeNullProps(obj) {
  if (Array.isArray(obj)) {
    return obj
      .map((item) => removeNullProps(item))
      .filter((item) => item !== null);
  } else if (typeof obj === "object" && obj !== null) {
    const newObj = {};
    for (let key in obj) {
      const value = removeNullProps(obj[key]);
      if (value !== null) {
        newObj[key] = value;
      }
    }
    return Object.keys(newObj).length > 0 ? newObj : null;
  }
  return obj;
}

/**
 * Sanitizes the given business partner name by removing special characters.
 *
 * This function takes a string input and removes any occurrences (not) of the following
 * a-z, A-Z, space and 0123456789 /-?:().,''+     /////Correct: /[^a-zA-Z0-9 /-?:().,'+]/g
 *
 * @param {string} str - The business partner name to be sanitized.
 * @returns {string} - A sanitized string with special characters removed.
 */

export const beneficiaryAndAddressFieldPattern = (msg) => ({
  //value: /^[a-zA-Z0-9\s\/\-?:().,'+]*$/, //Thats wrong
  value: /^[a-zA-Z0-9 /\-?:().,'+]*$/, //Correct: Latin alphabets, numbers, spaces, commas, dashes, and periods
  message: msg || "",
});

export const validateBusinessPartnerAndAddressField = (value) => {
  return beneficiaryAndAddressFieldPattern().value.test(value || "");
};

export const validateField = ({
  value,
  setError,
  clearErrors,
  fieldName,
  otherValidation = null,
}) => {
  const isValid = validateBusinessPartnerAndAddressField(value);

  if (!isValid || !value || otherValidation?.condition) {
    setError(
      fieldName,
      {
        type: "focus",
        message: !value
          ? "validation:errorMessage.thisFieldIsRequiredNotice"
          : !isValid
            ? "validation:errorMessage.specialCharactersNotAllowed"
            : otherValidation?.message
              ? otherValidation?.message
              : "",
      },
      { shouldFocus: true }
    );
    return false;
  } else {
    clearErrors(fieldName);
    return true;
  }
};

export const allowedProjectStatesForRoles = {
  //Please don't mix '*' with project states. It will work but it will be confusing.
  WAPreparer: ["FinancingAgreementEffective", "OnDisbursement"],
  WASubmitter: ["FinancingAgreementEffective", "OnDisbursement"],
  WAProjectReporting: ["FinancingAgreementEffective", "OnDisbursement"],
  ProcurementPlanner: ["*"], //* means all states
  ProcurementPreparer: ["*"],
  FARSubmitter: ["*"],
  ProjectTicketSubmitter: ["*"],
  ProjectTicketReporter: ["*"],
};

export const projectStateAllowsRole = (role, state) => {
  return (
    allowedProjectStatesForRoles?.[role]?.includes(state) ||
    allowedProjectStatesForRoles?.[role]?.includes("*")
  );
};

export const containsInvalidRolesForProjectState = (roles, state) => {
  let rolesNames = roles?.map((r) => r?.name);

  //console.log("rolesNames::", rolesNames);
  let testAll = rolesNames?.some((roleName) => {
    let testSingle = !projectStateAllowsRole(roleName, state);
    //console.log("testSingle::", testSingle, roleName, state);
    return testSingle;
  });

  return testAll;
};

export const allowedRolesForProjectState = (state) => {
  return Object.keys(allowedProjectStatesForRoles).filter(
    (role) =>
      allowedProjectStatesForRoles[role]?.includes(state) ||
      allowedProjectStatesForRoles[role]?.includes("*")
  );
};

export const allUserRolesWithApplications = [
  //  ProjectAccess users roles
  {
    displayName: "Financial Audit Report Submitter",
    name: "FARSubmitter",
    type: "ProjectAccess",
    applications: [
      {
        displayName: "e-Financial Management Platform",
        name: "e-Financial Management Platform",
        id: "finance",
      },
    ],
  },
  {
    displayName: "WA Submitter",
    name: "WASubmitter",
    type: "ProjectAccess",
    applications: [
      {
        displayName: "e-Disbursement Platform",
        name: "e-Disbursement Platform",
        id: "edp",
      },
    ],
  },
  {
    displayName: "WA Preparer",
    name: "WAPreparer",
    type: "ProjectAccess",
    applications: [
      {
        displayName: "e-Disbursement Platform",
        name: "e-Disbursement Platform",
        id: "edp",
      },
    ],
  },
  {
    displayName: "WA Project Reporting",
    name: "WAProjectReporting",
    type: "ProjectAccess",
    applications: [
      {
        displayName: "e-Disbursement Platform",
        name: "e-Disbursement Platform",
        id: "edp",
      },
    ],
  },
  {
    displayName: "Procurement Planner",
    name: "ProcurementPlanner",
    type: "ProjectAccess",
    applications: [
      {
        displayName: "e-Procurement Platform",
        name: "e-Procurement Platform",
        id: "epp",
      },
    ],
  },
  {
    displayName: "Procurement Preparer",
    name: "ProcurementPreparer",
    type: "ProjectAccess",
    applications: [
      {
        displayName: "e-Procurement Platform",
        name: "e-Procurement Platform",
        id: "epp",
      },
    ],
  },
  {
    displayName: "Project Ticket Submitter",
    name: "ProjectTicketSubmitter",
    type: "ProjectAccess",
    applications: [
      {
        displayName: "General Requests",
        name: "General Requests",
        id: "ticket",
      },
    ],
  },
  {
    displayName: "Project Ticket Reporter",
    name: "ProjectTicketReporter",
    type: "ProjectAccess",
    applications: [
      {
        displayName: "General Requests",
        name: "General Requests",
        id: "ticket",
      },
    ],
  },

  // project internal users roles
  {
    displayName: "WA Disbursement Officer",
    name: "WADisbursementOfficer",
    type: "ProjectTeamMembership",
    applications: [
      {
        displayName: "e-Disbursement Platform",
        name: "e-Disbursement Platform",
        id: "edp",
      },
    ],
  },
  {
    displayName: "Procurement Officer",
    name: "ProcurementOfficer",
    type: "ProjectTeamMembership",
    applications: [
      {
        displayName: "e-Procurement Portal",
        name: "e-Procurement Portal",
        id: "epp",
      },
    ],
  },
  {
    displayName: "Ticket Owner",
    name: "TicketOwner",
    type: "ProjectTeamMembership",
    applications: [
      {
        displayName: "General Requests",
        name: "General Requests",
        id: "ticket",
      },
    ],
  },
  {
    displayName: "Project Manager",
    name: "ProjectManager",
    type: "ProjectTeamMembership",
    applications: [
      {
        displayName: "e-Disbursement Platform",
        name: "e-Disbursement Platform",
        id: "edp",
      },
    ],
  },
  {
    displayName: "Country Manager",
    name: "CountryManager",
    type: "ProjectTeamMembership",
    applications: [
      {
        displayName: "General Requests",
        name: "General Requests",
        id: "ticket",
      },
    ],
  },
  {
    displayName: "Project Financial Management Specialist",
    name: "PFMSpecialist",
    type: "ProjectTeamMembership",
    applications: [
      {
        displayName: "FAR Portal",
        name: "FAR Portal",
        id: "finance",
      },
    ],
  },
  {
    displayName: "Project Ticket Receiver",
    name: "ProjectTicketReceiver",
    type: "ProjectTeamMembership",
    applications: [
      {
        displayName: "General Requests",
        name: "General Requests",
        id: "ticket",
      },
    ],
  },

  //RegionalProcurementOfficer
  {
    displayName: "Regional Procurement Officer",
    name: "RegionalProcurementOfficer",
    type: "ProjectTeamMembership",
    applications: [
      {
        displayName: "General Requests",
        name: "General Requests",
        id: "ticket",
      },
    ],
  },

  //GlobalProcurementOfficer
  {
    displayName: "Global Procurement Officer",
    name: "GlobalProcurementOfficer",
    type: "ProjectTeamMembership",
    applications: [
      {
        displayName: "General Requests",
        name: "General Requests",
        id: "ticket",
      },
    ],
  },

  //OwnerDivisionManager
  {
    displayName: "Owner Division Manager",
    name: "OwnerDivisionManager",
    type: "ProjectTeamMembership",
    applications: [
      {
        displayName: "General Requests",
        name: "General Requests",
        id: "ticket",
      },
    ],
  },

  //ImplementationDivisionManager
  {
    displayName: "Implementation Division Manager",
    name: "ImplementationDivisionManager",
    type: "ProjectTeamMembership",
    applications: [
      {
        displayName: "General Requests",
        name: "General Requests",
        id: "ticket",
      },
    ],
  },

  // global internal users roles
  {
    displayName: "IT Admin",
    name: "ITAdmin",
    type: "SystemRole",
    applications: [
      {
        displayName: "Administration",
        name: "Administration",
        id: "admin",
      },
    ],
  },
  {
    displayName: "WA Finance Admin",
    name: "WAFinanceAdmin",
    type: "SystemRole",
    applications: [
      {
        displayName: "e-Disbursement Platform",
        name: "e-Disbursement Platform",
        id: "edp",
      },
    ],
  },
  {
    displayName: "WA Reporting",
    name: "WAReporting",
    type: "SystemRole",
    applications: [
      {
        displayName: "e-Disbursement Platform",
        name: "e-Disbursement Platform",
        id: "edp",
      },
    ],
  },
  {
    displayName: "EPP Reporting",
    name: "EPPReporter",
    type: "SystemRole",
    applications: [
      {
        displayName: "e-Procurement Portal",
        name: "e-Procurement Portal",
        id: "epp",
      },
    ],
  },
  {
    displayName: "Procurement Admin",
    name: "ProcurementAdmin",
    type: "SystemRole",
    applications: [
      {
        displayName: "e-Procurement Portal",
        name: "e-Procurement Portal",
        id: "epp",
      },
    ],
  },
  {
    displayName: "FAR Reporting",
    name: "FARReporter",
    type: "SystemRole",
    applications: [
      {
        displayName: "FAR Portal",
        name: "FAR Portal",
        id: "finance",
      },
    ],
  },
  {
    displayName: "Financial Audit Report Admin",
    name: "FARAdmin",
    type: "SystemRole",
    applications: [
      {
        displayName: "FAR Portal",
        name: "FAR Portal",
        id: "finance",
      },
    ],
  },
  {
    displayName: "General Country Request Reporting",
    name: "CountryTicketReporter",
    type: "SystemRole",
    applications: [
      {
        displayName: "General Requests",
        name: "General Requests",
        id: "ticket",
      },
    ],
  },
  {
    displayName: "General Project Request Reporting",
    name: "ProjectTicketReporter",
    type: "SystemRole",
    applications: [
      {
        displayName: "General Requests",
        name: "General Requests",
        id: "ticket",
      },
    ],
  },
  {
    displayName: "Country Ticket Receiver",
    name: "CountryTicketReceiver",
    type: "SystemRole",
    applications: [
      {
        displayName: "General Requests",
        name: "General Requests",
        id: "ticket",
      },
    ],
  },
  {
    displayName: "Ticketing Admin",
    name: "TicketingAdmin",
    type: "SystemRole",
    applications: [
      {
        displayName: "General Requests",
        name: "General Requests",
        id: "ticket",
      },
    ],
  },
  //  {
  //    displayName: "CG General User",
  //    name: "CGUser",
  //    type: "SystemRole",
  //    applications: [
  //      {
  //        displayName: "General Requests",
  //        name: "General Requests",
  //        id: "ticket",
  //      },
  //    ],
  //  },
];

export const originOptions = [
  {
    label: "Project Team Membership",
    value: "ProjectTeamMembership",
    visibleRoles: ["ITAdmin"],
  },
  {
    label: "Access Request",
    value: "AccessRequest",
  },
  {
    label: "Substitution Rule",
    value: "SubstitutionRule",
    visibleRoles: ["ITAdmin"],
  },
  {
    label: "Admin",
    value: "Admin",
  },
];

export const accessStatusOptions = {
  all: {
    label: "All",
    value: "all",
  },
  active: {
    label: "Active",
    value: "active",
  },
  revoked: {
    label: "Revoked",
    value: "archived",
  },
};

export const preventInvalidDate = (value) => {
  if (!value || !moment(value).isValid()) {
    //console.log("Date is not legit or it is null::", value);
    return null;
  }
  //console.log("Date is legit::", value);
  return value;
};

export const getOmsDiagramUrl = (processInstanceId) =>
  `${config?.environment?.omsUri}/bpm-process/index.html?processInstanceId=${processInstanceId}`;

export const sanitizeHtmlContent = (htmlBody) => {
  if (!htmlBody || typeof htmlBody !== "string") {
    return "";
  }

  // Dynamically import DOMPurify to avoid issues if not installed
  try {
    return DOMPurify.sanitize(htmlBody, {
      ALLOWED_TAGS: [
        "table",
        "thead",
        "tbody",
        "tr",
        "td",
        "th",
        "label",
        "p",
        "br",
        "strong",
        "em",
        "u",
        "s",
        "a",
        "ul",
        "ol",
        "li",
        "h1",
        "h2",
        "h3",
        "h4",
        "h5",
        "h6",
        "blockquote",
        "code",
        "pre",
        "span",
        "div",
        "b",
        "i",
        "sub",
        "sup",
        "mark",
        "small",
        "del",
        "ins",
        "button",
        "footer",
        "header",
        "main",
        "nav",
        "section",
        "article",
        "aside",
        "details",
        "summary",
        "dialog",
        "figure",
        "figcaption",
        "audio",
        "video",
      ],
      ALLOWED_ATTR: ["href", "title", "class", "id", "data-*"],
      ALLOW_DATA_ATTR: true,
      FORBID_TAGS: ["script", "iframe", "object", "embed"],
      FORBID_ATTR: [
        "onerror",
        "onload",
        "onclick",
        "onmouseover",
        "onmouseout",
        "onfocus",
        "onblur",
        "onchange",
        "onsubmit",
        "onreset",
        "onselect",
        "onkeydown",
        "onkeypress",
        "onkeyup",
        "onabort",
        "oncanplay",
        "oncanplaythrough",
        "ondurationchange",
        "onemptied",
        "onended",
        "onerror",
        "onloadeddata",
        "onloadedmetadata",
        "onloadstart",
        "onpause",
        "onplay",
        "onplaying",
        "onprogress",
        "onratechange",
        "onseeked",
        "onseeking",
        "onstalled",
        "onsuspend",
        "ontimeupdate",
        "onvolumechange",
        "onwaiting",
      ],
    });
  } catch (error) {
    // If DOMPurify is not available, return empty string or basic sanitization
    console.warn(
      "DOMPurify is not available. HTML content not sanitized.",
      error
    );
    // Basic fallback: remove script tags and common event handlers
    return htmlBody
      .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, "")
      .replace(/\s*on\w+\s*=\s*["'][^"']*["']/gi, "")
      .replace(/\s*on\w+\s*=\s*[^\s>]*/gi, "");
  }
};

/**
 * Sanitizes a string to plain text by stripping all HTML tags and attributes.
 * Use for user-displayed text (e.g. names) to prevent XSS.
 * @param {string} str - The string to sanitize.
 * @returns {string} - Plain text with no HTML, or empty string if input is invalid.
 */
export const sanitizePlainText = (str) => {
  if (str == null || typeof str !== "string") {
    return "";
  }
  try {
    return DOMPurify.sanitize(str, { ALLOWED_TAGS: [] });
  } catch (error) {
    console.warn("DOMPurify plain text sanitization failed.", error);
    return str.replace(/<[^>]*>/g, "");
  }
};

export const getExcelFilenameSuffix = (filename) => {
  const date = new Date();
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  const hour = String(date.getHours()).padStart(2, "0");
  const minute = String(date.getMinutes()).padStart(2, "0");

  let currentEnv = "";
  if (isDev(null, "location")) {
    currentEnv = "DEV";
  } else if (isTest(null, "location")) {
    currentEnv = "QA";
  } else if (isProd(null, "location")) {
    currentEnv = "PRD";
  }

  return `${filename}_${currentEnv}_${year}${month}${day}_${hour}.${minute}`;
};
